import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePortfolios } from "../context/PortfolioContext";
import BrandLogo from "../components/BrandLogo";
import { TechWatermark } from "../components/TechWatermark";
import {
  apiListClients,
  apiCreateClient,
  apiUpdateClient,
  apiDeleteClient,
  apiAddPayment,
  apiDeletePayment,
  apiListNotifications,
  apiRefreshNotifications,
  apiMarkAllRead,
  apiDeleteNotification,
} from "../api";
import { generateReceiptPDF } from "../lib/receipt";
import {
  enablePush,
  notificationPermission,
  pushSupported,
  isStandalone,
  registerServiceWorker,
} from "../lib/push";
import {
  ArrowLeft,
  Plus,
  Users,
  CalendarClock,
  AlertTriangle,
  Infinity as InfinityIcon,
  Bell,
  BellRing,
  Pencil,
  Trash2,
  FileText,
  MessageCircle,
  Search,
  X,
  CheckCheck,
  Wallet,
  BadgeCheck,
  FileSignature,
  Trash,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import ContractsTab from "../components/contracts/ContractsTab";
import { toast } from "../hooks/use-toast";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(String(iso).length <= 10 ? iso + "T00:00:00" : iso).toLocaleDateString("es-MX", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

const emptyForm = () => ({
  name: "",
  whatsapp: "",
  start_date: new Date().toISOString().slice(0, 10),
  billing_type: "annual",
  amount: "",
  currency: "MXN",
  portfolio_id: "",
  notes: "",
});

const StatCard = ({ icon: Icon, label, value, tone }) => (
  <div
    className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
    data-testid={`admin-stat-${label}`}
  >
    <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${tone}`}>
      <Icon className="h-5 w-5" />
    </span>
    <div>
      <p className="text-xl font-bold text-slate-900">{value}</p>
      <p className="text-xs text-slate-500">{label}</p>
    </div>
  </div>
);

const RenewalChip = ({ client }) => {
  if (client.billing_type === "permanent" || client.state === "permanent") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">
        <InfinityIcon className="h-3.5 w-3.5" /> Permanente
      </span>
    );
  }
  if (client.state === "paid") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700">
        <BadgeCheck className="h-3.5 w-3.5" /> Pagado {client.current_period}
      </span>
    );
  }
  if (client.state === "overdue") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-1 text-xs font-semibold text-red-700">
        <AlertTriangle className="h-3.5 w-3.5" /> Vencida {fmtDate(client.renewal_date)}
      </span>
    );
  }
  if (client.state === "due_soon") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-700">
        <CalendarClock className="h-3.5 w-3.5" />
        {client.days_until === 0 ? "Vence hoy" : `Vence en ${client.days_until} día${client.days_until === 1 ? "" : "s"}`}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700">
      <CalendarClock className="h-3.5 w-3.5" /> Renueva {fmtDate(client.renewal_date)}
    </span>
  );
};

const Admin = () => {
  const navigate = useNavigate();
  const { portfolios } = usePortfolios();

  const [clients, setClients] = useState([]);
  const [tab, setTab] = useState("clients");
  const [contractClient, setContractClient] = useState("");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [payTarget, setPayTarget] = useState(null);
  const [payForm, setPayForm] = useState(null);
  const [paySaving, setPaySaving] = useState(false);

  const [notifs, setNotifs] = useState([]);
  const [pushState, setPushState] = useState("idle");
  const mounted = useRef(true);

  const loadClients = async () => {
    try {
      const list = await apiListClients();
      if (mounted.current) setClients(list || []);
    } catch (e) {
      /* noop */
    } finally {
      if (mounted.current) setLoading(false);
    }
  };

  const loadNotifs = async () => {
    try {
      await apiRefreshNotifications();
    } catch (e) {
      /* noop */
    }
    try {
      const list = await apiListNotifications();
      if (mounted.current) setNotifs(list || []);
    } catch (e) {
      /* noop */
    }
  };

  useEffect(() => {
    mounted.current = true;
    registerServiceWorker().catch(() => {});
    loadClients();
    loadNotifs();
    return () => {
      mounted.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stats = useMemo(() => {
    return {
      total: clients.length,
      dueSoon: clients.filter((c) => c.state === "due_soon").length,
      overdue: clients.filter((c) => c.state === "overdue").length,
      permanent: clients.filter((c) => c.billing_type === "permanent").length,
      paid: clients.filter((c) => c.state === "paid").length,
    };
  }, [clients]);

  const unread = notifs.filter((n) => !n.read).length;

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    const sorted = [...clients].sort((a, b) => {
      const rank = (c) => (c.state === "overdue" ? 0 : c.state === "due_soon" ? 1 : c.state === "ok" ? 2 : 3);
      return rank(a) - rank(b);
    });
    if (!q) return sorted;
    return sorted.filter(
      (c) =>
        (c.name || "").toLowerCase().includes(q) ||
        (c.whatsapp || "").toLowerCase().includes(q)
    );
  }, [clients, query]);

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm());
    setFormOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name || "",
      whatsapp: c.whatsapp || "",
      start_date: (c.start_date || "").slice(0, 10) || new Date().toISOString().slice(0, 10),
      billing_type: c.billing_type || "annual",
      amount: c.amount ?? "",
      currency: c.currency || "MXN",
      portfolio_id: c.portfolio_id || "",
      notes: c.notes || "",
    });
    setFormOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast({ title: "Falta el nombre del cliente", variant: "destructive" });
      return;
    }
    setSaving(true);
    const payload = { ...form, amount: form.amount === "" ? null : Number(form.amount) };
    try {
      if (editing) {
        const updated = await apiUpdateClient(editing.id, { ...editing, ...payload });
        setClients((prev) => prev.map((c) => (c.id === editing.id ? updated : c)));
        toast({ title: "Cliente actualizado" });
      } else {
        const created = await apiCreateClient(payload);
        setClients((prev) => [created, ...prev]);
        toast({ title: "Cliente agregado" });
      }
      setFormOpen(false);
      loadNotifs();
    } catch (e) {
      toast({ title: "No se pudo guardar", description: "Revisa tu conexión", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    const id = deleteTarget.id;
    setClients((prev) => prev.filter((c) => c.id !== id));
    setDeleteTarget(null);
    try {
      await apiDeleteClient(id);
      toast({ title: "Cliente eliminado" });
    } catch (e) {
      /* noop */
    }
  };

  const downloadReceipt = async (c) => {
    try {
      await generateReceiptPDF(c, { renewal_date: c.renewal_date });
      toast({ title: "Comprobante generado", description: "PDF descargado con el logo UAICARD." });
    } catch (e) {
      toast({ title: "No se pudo generar el PDF", variant: "destructive" });
    }
  };

  const openWhatsApp = (c) => {
    const num = (c.whatsapp || "").replace(/\D/g, "");
    if (!num) {
      toast({ title: "Este cliente no tiene WhatsApp" });
      return;
    }
    window.open(`https://wa.me/${num}`, "_blank");
  };

  const openPayments = (c) => {
    setPayTarget(c);
    setPayForm({
      amount: c.amount ?? "",
      period: c.current_period || new Date().getFullYear(),
      date: new Date().toISOString().slice(0, 10),
      method: "",
      note: "",
    });
  };

  const syncClient = (updated) => {
    setClients((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
    setPayTarget((prev) => (prev && prev.id === updated.id ? updated : prev));
  };

  const addPayment = async () => {
    if (!payTarget) return;
    setPaySaving(true);
    try {
      const updated = await apiAddPayment(payTarget.id, {
        amount: payForm.amount === "" ? null : Number(payForm.amount),
        period: Number(payForm.period),
        date: payForm.date,
        method: payForm.method,
        note: payForm.note,
      });
      syncClient(updated);
      setNotifs((prev) => prev.filter((n) => n.client_id !== payTarget.id || n.type !== "renewal"));
      toast({ title: "Pago registrado", description: `Renovación ${payForm.period} marcada como pagada.` });
      setPayForm((f) => ({ ...f, method: "", note: "" }));
    } catch (e) {
      toast({ title: "No se pudo registrar el pago", variant: "destructive" });
    } finally {
      setPaySaving(false);
    }
  };

  const removePayment = async (pid) => {
    if (!payTarget) return;
    try {
      const updated = await apiDeletePayment(payTarget.id, pid);
      syncClient(updated);
    } catch (e) {
      /* noop */
    }
  };

  const handleEnablePush = async () => {
    setPushState("loading");
    const res = await enablePush();
    if (res.ok) {
      setPushState("granted");
      toast({ title: "Notificaciones activadas", description: "Te llegará un aviso de prueba ahora." });
    } else if (res.reason === "denied") {
      setPushState("denied");
      toast({
        title: "Permiso rechazado",
        description: "Actívalas desde los ajustes del navegador/teléfono.",
        variant: "destructive",
      });
    } else if (res.reason === "unsupported") {
      setPushState("unsupported");
      toast({
        title: "Agrega la app a tu pantalla de inicio",
        description: "En iPhone, abre con Safari → Compartir → Agregar a inicio, y vuelve a intentar.",
      });
    } else {
      setPushState("error");
      toast({ title: "No se pudo activar", variant: "destructive" });
    }
  };

  const markRead = async () => {
    setNotifs((prev) => prev.map((n) => ({ ...n, read: true })));
    try {
      await apiMarkAllRead();
    } catch (e) {
      /* noop */
    }
  };

  const removeNotif = async (id) => {
    setNotifs((prev) => prev.filter((n) => n.id !== id));
    try {
      await apiDeleteNotification(id);
    } catch (e) {
      /* noop */
    }
  };

  const perm = notificationPermission();
  const pushLabel =
    perm === "granted" || pushState === "granted"
      ? "Notificaciones activas"
      : "Activar notificaciones";

  return (
    <div className="relative min-h-screen bg-[#0b1530]">
      <TechWatermark />
      <header className="safe-top sticky top-0 z-30 border-b border-white/10 bg-white/95 shadow-lg shadow-black/20 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 pb-4 pt-7 sm:px-6 sm:pt-5">
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/")}
              className="rounded-lg p-2 text-slate-500 transition hover:bg-slate-100"
              data-testid="admin-back-button"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <BrandLogo className="h-10 w-auto min-w-0 max-w-[46vw] object-contain object-left sm:h-12" />
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {/* Notifications bell */}
            <Popover>
              <PopoverTrigger asChild>
                <button
                  className="relative rounded-xl border border-slate-200 bg-white p-2.5 text-slate-600 transition hover:bg-slate-50"
                  data-testid="admin-bell-button"
                >
                  <Bell className="h-5 w-5" />
                  {unread > 0 && (
                    <span className="absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1 text-[11px] font-bold text-white">
                      {unread}
                    </span>
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 p-0">
                <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
                  <span className="font-semibold text-slate-900">Avisos</span>
                  {notifs.length > 0 && (
                    <button
                      onClick={markRead}
                      className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700"
                      data-testid="admin-mark-read"
                    >
                      <CheckCheck className="h-3.5 w-3.5" /> Marcar leídas
                    </button>
                  )}
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {notifs.length === 0 ? (
                    <p className="px-4 py-8 text-center text-sm text-slate-400">
                      Sin avisos por ahora.
                    </p>
                  ) : (
                    notifs.map((n) => (
                      <div
                        key={n.id}
                        className={`flex items-start gap-2 border-b border-slate-50 px-4 py-3 ${
                          n.read ? "bg-white" : "bg-blue-50/60"
                        }`}
                        data-testid="admin-notif-item"
                      >
                        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-600">
                          <CalendarClock className="h-4 w-4" />
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-slate-800">{n.title}</p>
                          <p className="text-xs text-slate-500">{n.body}</p>
                        </div>
                        <button
                          onClick={() => removeNotif(n.id)}
                          className="rounded p-1 text-slate-300 hover:text-slate-500"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </PopoverContent>
            </Popover>

            <Button onClick={openNew} size="sm" className="gap-1.5 bg-blue-600 hover:bg-blue-700" data-testid="admin-add-client">
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">Nuevo cliente</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-8">
        <div className="mb-6">
          <h1 className="font-display text-2xl font-bold text-white sm:text-3xl">
            Cartera de clientes
          </h1>
          <p className="mt-1 text-sm text-blue-100/70">
            Administra los perfiles entregados, sus renovaciones anuales y descarga comprobantes.
          </p>
        </div>

        {/* Push banner */}
        <div
          className={`mb-6 flex flex-col items-start gap-3 rounded-2xl border p-4 sm:flex-row sm:items-center sm:justify-between ${
            perm === "granted"
              ? "border-emerald-200 bg-emerald-50"
              : "border-blue-200 bg-blue-50"
          }`}
        >
          <div className="flex items-start gap-3">
            <span
              className={`flex h-10 w-10 items-center justify-center rounded-xl ${
                perm === "granted" ? "bg-emerald-100 text-emerald-600" : "bg-blue-100 text-blue-600"
              }`}
            >
              <BellRing className="h-5 w-5" />
            </span>
            <div>
              <p className="text-sm font-semibold text-slate-900">
                {perm === "granted" ? "Notificaciones activas en este dispositivo" : "Activa las notificaciones"}
              </p>
              <p className="text-xs text-slate-500">
                Recibe un aviso 15 días antes de que venza la anualidad de cada cliente.
                {!isStandalone() && pushSupported() === false
                  ? " En iPhone: agrega la app a tu pantalla de inicio para recibirlas."
                  : ""}
              </p>
            </div>
          </div>
          <Button
            onClick={handleEnablePush}
            disabled={pushState === "loading" || perm === "granted"}
            className={perm === "granted" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-blue-600 hover:bg-blue-700"}
            data-testid="admin-enable-push"
          >
            {pushState === "loading" ? "Activando..." : pushLabel}
          </Button>
        </div>

        <div className="mb-6 inline-flex rounded-xl bg-slate-100 p-1" data-testid="admin-tabs">
          {[["clients", "Clientes"], ["contracts", "Contratos"]].map(([k, l]) => (
            <button
              key={k}
              onClick={() => {
                setTab(k);
                if (k === "clients") setContractClient("");
              }}
              className={`rounded-lg px-5 py-2 text-sm font-semibold transition ${tab === k ? "bg-white text-slate-900 shadow" : "text-slate-500 hover:text-slate-700"}`}
              data-testid={`admin-tab-${k}`}
            >
              {l}
            </button>
          ))}
        </div>

        {tab === "contracts" ? (
          <ContractsTab clients={clients} clientFilter={contractClient} setClientFilter={setContractClient} />
        ) : (
        <>
        <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-5">
          <StatCard icon={Users} label="Clientes" value={stats.total} tone="bg-blue-50 text-blue-600" />
          <StatCard icon={BadgeCheck} label="Pagados" value={stats.paid} tone="bg-emerald-50 text-emerald-600" />
          <StatCard icon={CalendarClock} label="Por vencer (15d)" value={stats.dueSoon} tone="bg-amber-50 text-amber-600" />
          <StatCard icon={AlertTriangle} label="Vencidos" value={stats.overdue} tone="bg-red-50 text-red-600" />
          <StatCard icon={InfinityIcon} label="Permanentes" value={stats.permanent} tone="bg-slate-100 text-slate-600" />
        </div>

        <div className="mb-5 relative max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por nombre o WhatsApp..."
            className="bg-white pl-9"
            data-testid="admin-search"
          />
        </div>

        {loading ? (
          <p className="py-12 text-center text-blue-100/70">Cargando clientes...</p>
        ) : filtered.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-white/95 py-16 text-center">
            <Users className="mx-auto mb-3 h-10 w-10 text-slate-300" />
            <p className="font-medium text-slate-600">Aún no tienes clientes registrados</p>
            <p className="mb-4 text-sm text-slate-400">Agrega tu primer cliente para llevar su control.</p>
            <Button onClick={openNew} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="mr-1.5 h-4 w-4" /> Nuevo cliente
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {filtered.map((c) => (
              <div
                key={c.id}
                className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:shadow-md"
                data-testid="admin-client-card"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="truncate font-display text-lg font-bold text-slate-900">{c.name}</h3>
                    <p className="truncate text-sm text-slate-500">{c.whatsapp || "Sin WhatsApp"}</p>
                  </div>
                  <RenewalChip client={c} />
                </div>

                <div className="grid grid-cols-2 gap-2 text-xs text-slate-500">
                  <div>
                    <p className="text-slate-400">Inicio</p>
                    <p className="font-medium text-slate-700">{fmtDate(c.start_date)}</p>
                  </div>
                  <div>
                    <p className="text-slate-400">Monto</p>
                    <p className="font-medium text-slate-700">
                      {c.amount ? `$${Number(c.amount).toLocaleString("es-MX")} ${c.currency || "MXN"}` : "—"}
                    </p>
                  </div>
                </div>

                <div className="mt-auto flex flex-wrap gap-2 pt-2">
                  <Button size="sm" variant="outline" className="gap-1.5" onClick={() => downloadReceipt(c)} data-testid="admin-receipt-btn">
                    <FileText className="h-4 w-4" /> Comprobante
                  </Button>
                  <Button size="sm" variant="outline" className="gap-1.5 text-emerald-600" onClick={() => openWhatsApp(c)}>
                    <MessageCircle className="h-4 w-4" /> WhatsApp
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1.5"
                    onClick={() => {
                      setContractClient(c.id);
                      setTab("contracts");
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    data-testid="admin-client-contracts-btn"
                  >
                    <FileSignature className="h-4 w-4" /> Contratos
                  </Button>
                  <Button size="sm" variant="outline" className="gap-1.5" onClick={() => openPayments(c)} data-testid="admin-payments-btn">
                    <Wallet className="h-4 w-4" /> Pagos
                    {(c.payments || []).length > 0 && (
                      <span className="ml-0.5 rounded-full bg-slate-100 px-1.5 text-[11px] font-bold text-slate-600">
                        {(c.payments || []).length}
                      </span>
                    )}
                  </Button>
                  <Button size="sm" variant="outline" className="gap-1.5" onClick={() => openEdit(c)} data-testid="admin-edit-btn">
                    <Pencil className="h-4 w-4" /> Editar
                  </Button>
                  <Button size="sm" variant="outline" className="gap-1.5 text-red-600" onClick={() => setDeleteTarget(c)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
        </>
        )}
      </main>

      {/* Add / Edit dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar cliente" : "Nuevo cliente"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="c-name">Nombre *</Label>
              <Input
                id="c-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Nombre del cliente o negocio"
                data-testid="admin-form-name"
              />
            </div>
            <div>
              <Label htmlFor="c-wa">WhatsApp</Label>
              <Input
                id="c-wa"
                value={form.whatsapp}
                onChange={(e) => setForm((f) => ({ ...f, whatsapp: e.target.value }))}
                placeholder="52 1 222 123 4567"
                inputMode="tel"
                data-testid="admin-form-whatsapp"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="c-start">Fecha de inicio</Label>
                <Input
                  id="c-start"
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))}
                  data-testid="admin-form-startdate"
                />
              </div>
              <div>
                <Label>Modalidad</Label>
                <Select value={form.billing_type} onValueChange={(v) => setForm((f) => ({ ...f, billing_type: v }))}>
                  <SelectTrigger data-testid="admin-form-billing">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="annual">Renovación anual</SelectItem>
                    <SelectItem value="permanent">Permanente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="c-amount">Monto</Label>
                <Input
                  id="c-amount"
                  type="number"
                  value={form.amount}
                  onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                  placeholder="0.00"
                  data-testid="admin-form-amount"
                />
              </div>
              <div>
                <Label>Portafolio ligado</Label>
                <Select
                  value={form.portfolio_id || "none"}
                  onValueChange={(v) => setForm((f) => ({ ...f, portfolio_id: v === "none" ? "" : v }))}
                >
                  <SelectTrigger data-testid="admin-form-portfolio">
                    <SelectValue placeholder="Opcional" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Ninguno</SelectItem>
                    {portfolios.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="c-notes">Notas</Label>
              <Textarea
                id="c-notes"
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                placeholder="Detalles del servicio, acuerdos, etc."
                rows={3}
                data-testid="admin-form-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)}>
              Cancelar
            </Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={save} disabled={saving} data-testid="admin-form-save">
              {saving ? "Guardando..." : editing ? "Guardar cambios" : "Agregar cliente"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payments dialog */}
      <Dialog open={!!payTarget} onOpenChange={(o) => !o && setPayTarget(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Historial de pagos · {payTarget?.name}</DialogTitle>
          </DialogHeader>

          {payTarget && payForm && (
            <>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Registrar pago
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label htmlFor="p-period" className="text-xs">Año / renovación</Label>
                    <Input
                      id="p-period"
                      type="number"
                      value={payForm.period}
                      onChange={(e) => setPayForm((f) => ({ ...f, period: e.target.value }))}
                      data-testid="payment-period"
                    />
                  </div>
                  <div>
                    <Label htmlFor="p-amount" className="text-xs">Monto</Label>
                    <Input
                      id="p-amount"
                      type="number"
                      value={payForm.amount}
                      onChange={(e) => setPayForm((f) => ({ ...f, amount: e.target.value }))}
                      placeholder="0.00"
                      data-testid="payment-amount"
                    />
                  </div>
                  <div>
                    <Label htmlFor="p-date" className="text-xs">Fecha de pago</Label>
                    <Input
                      id="p-date"
                      type="date"
                      value={payForm.date}
                      onChange={(e) => setPayForm((f) => ({ ...f, date: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="p-method" className="text-xs">Método</Label>
                    <Input
                      id="p-method"
                      value={payForm.method}
                      onChange={(e) => setPayForm((f) => ({ ...f, method: e.target.value }))}
                      placeholder="Efectivo, transfer..."
                    />
                  </div>
                </div>
                <Button
                  className="mt-3 w-full gap-1.5 bg-emerald-600 hover:bg-emerald-700"
                  onClick={addPayment}
                  disabled={paySaving}
                  data-testid="payment-save"
                >
                  <CheckCheck className="h-4 w-4" />
                  {paySaving ? "Guardando..." : `Marcar pagado ${payForm.period}`}
                </Button>
              </div>

              <div className="mt-2">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Pagos registrados
                </p>
                {(payTarget.payments || []).length === 0 ? (
                  <p className="py-6 text-center text-sm text-slate-400">
                    Aún no hay pagos registrados.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {[...(payTarget.payments || [])]
                      .sort((a, b) => (b.period || 0) - (a.period || 0))
                      .map((p) => (
                        <div
                          key={p.id}
                          className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3"
                          data-testid="payment-row"
                        >
                          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
                            <BadgeCheck className="h-5 w-5" />
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-slate-800">
                              Renovación {p.period}
                              {p.amount ? ` · $${Number(p.amount).toLocaleString("es-MX")} ${p.currency || "MXN"}` : ""}
                            </p>
                            <p className="truncate text-xs text-slate-500">
                              {fmtDate(p.date)}
                              {p.method ? ` · ${p.method}` : ""}
                              {p.note ? ` · ${p.note}` : ""}
                            </p>
                          </div>
                          <button
                            onClick={() => removePayment(p.id)}
                            className="rounded p-1.5 text-slate-300 transition hover:text-red-500"
                            data-testid="payment-delete"
                          >
                            <Trash className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayTarget(null)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar a {deleteTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará de tu cartera de clientes. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={doDelete}>
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Admin;
